import type { IGetActionsData } from "@api";
import CloseIcon from "@mui/icons-material/Close";
import { Button, DialogActions, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DialogTitle } from "@mui/material";
import { useStableCallback } from "@root/utils/hooks/useStableCallback";
import { Dialog } from "@styleguide";
import { type KeyboardEvent, useEffect } from "react";
import { ModalActionForm } from "./form";
import { useModalAction } from "./logics";
import type { IModalActionResolver, TModalAction } from "./logics/types";

export interface IModalActionOptions {
  hideCandidateField?: boolean;
  hideOwnerField?: boolean;
  hideConvocationText?: boolean;
  disabledActionTypes?: string[];
  disableActionTypeField?: boolean;
  hideBeneficiaryComment?: boolean;
  hideApproachField?: boolean;
  disableCandidateField?: boolean;
}

export type TModalActionProps = {
  open: boolean;
  onSubmit?: (action: IGetActionsData) => void;
  onClose: () => void;
  action?: TModalAction;
  options?: IModalActionOptions;
  title: string;
  subtitle?: string;
  overrideSubmit?: (action: IModalActionResolver) => void | Promise<void>;
};

export const ModalAction = ({
  open,
  onSubmit,
  onClose,
  action,
  options,
  title,
  subtitle,
  overrideSubmit,
}: TModalActionProps) => {
  const { control, handleSubmit, setValue, loading } = useModalAction(open, action, onSubmit, overrideSubmit, options);
  const theme = useTheme();
  const tablet = useMediaQuery(theme.breakpoints.up("tablet"));

  const keyDownListener = useStableCallback((evt: KeyboardEvent<HTMLFormElement>) => {
    if ((evt.ctrlKey || evt.metaKey) && evt.key === "Enter" && !loading) {
      evt.preventDefault();
      void handleSubmit();
    }
  });

  const documentKeyDownListener = useStableCallback((evt: globalThis.KeyboardEvent) => {
    if ((evt.ctrlKey || evt.metaKey) && evt.key === "Enter" && !loading) {
      evt.preventDefault();
      void handleSubmit();
    }
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    document.addEventListener("keydown", documentKeyDownListener);
    return () => document.removeEventListener("keydown", documentKeyDownListener);
  }, [open, documentKeyDownListener]);

  return (
    <Dialog
      open={open}
      fullScreen={!tablet}
      disableEnforceFocus
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit,
          onKeyDown: keyDownListener,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4">{title}</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Stack>
        <Typography variant="body1">{subtitle}</Typography>
      </DialogTitle>
      <ModalActionForm control={control} setValue={setValue} action={action} options={options} />
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button variant="contained" color="primary" type="submit" disabled={loading} loading={loading}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export * from "./multipleCandidates";
